/* ============================================================================
   wd.js — the global behaviour of the WRAPDUO theme. No framework, no build.

   Progressive enhancement throughout: every hook below upgrades a control
   that already works as a link or a form. gates/controls.mjs PARSES the
   data-* hooks out of this file and fails any rendered button that is bound
   to none of them, so renaming a hook here without renaming it in Liquid
   fails the build instead of shipping a dead button (SOA's controls-test).

   Contents: sheets · cart (Ajax API + Section Rendering) · quick add ·
   card swatches · predictive search · header disclosure · announcement ·
   hero slideshow · localisation autosubmit · toast.
   ========================================================================== */
(() => {
  'use strict';
  const WD = (window.WD = window.WD || {});
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const store = {
    get(k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { window.localStorage.setItem(k, v); } catch (e) { /* private mode */ } },
  };

  /* ── toast ─────────────────────────────────────────────────────────────── */
  let toastTimer;
  WD.toast = (msg) => {
    const t = $('[data-toast]');
    if (!t) return;
    t.textContent = msg;
    t.classList.add('is-on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => t.classList.remove('is-on'), 2600);
  };

  /* ── sheets (<dialog>) ─────────────────────────────────────────────────── */
  let lastOpener = null;
  const lockScroll = (on) => {
    if (on) { document.body.dataset.scrollLocked = ''; document.body.style.top = `-${window.scrollY}px`; }
    else if ('scrollLocked' in document.body.dataset) {
      const y = -parseInt(document.body.style.top || '0', 10);
      delete document.body.dataset.scrollLocked; document.body.style.top = ''; window.scrollTo(0, y);
    }
  };
  WD.openSheet = (id, opener) => {
    const d = document.getElementById(id);
    if (!d || typeof d.showModal !== 'function') return false;
    $$('dialog[open][data-sheet]').forEach((o) => o !== d && WD.closeSheet(o));
    lastOpener = opener || document.activeElement;
    if (!d.open) d.showModal();
    lockScroll(true);
    const focusable = d.querySelector('[data-predictive-input]') || d.querySelector('[data-sheet-close]');
    focusable && focusable.focus();
    if (opener) opener.setAttribute('aria-expanded', 'true');
    return true;
  };
  WD.closeSheet = (d) => {
    if (!d || !d.open) return;
    d.close();
    lockScroll(false);
    $$(`[aria-controls="${d.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
    if (lastOpener && document.contains(lastOpener)) lastOpener.focus();
  };
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-sheet-open]');
    if (opener) {
      if (WD.openSheet(opener.dataset.sheetOpen, opener)) e.preventDefault();
      return;
    }
    const closer = e.target.closest('[data-sheet-close]');
    if (closer) { WD.closeSheet(closer.closest('dialog')); return; }
    // click on the backdrop (the dialog itself, outside its panel)
    if (e.target.matches('dialog[data-sheet]')) WD.closeSheet(e.target);
  });
  // A type=search input consumes the first Escape to clear itself, so the
  // dialog's own cancel never fires from the search box. Close explicitly.
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const open = $$('dialog[open][data-sheet]').pop();
    if (open) { e.preventDefault(); WD.closeSheet(open); }
  });
  document.addEventListener('close', (e) => { if (e.target.matches?.('dialog[data-sheet]')) lockScroll(false); }, true);

  /* ── money ─────────────────────────────────────────────────────────────── */
  WD.money = (cents) => {
    const n = (Number(cents) || 0) / 100;
    const fmt = WD.moneyFormat || '€{{amount}}';
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => {
      if (k === 'amount_with_comma_separator') return n.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      if (k === 'amount_no_decimals') return Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    });
  };

  /* ── cart ──────────────────────────────────────────────────────────────── */
  const drawer = () => $('[data-cart-drawer]');
  const drawerSection = () => drawer()?.dataset.sectionId;
  const cartSections = () => {
    const ids = [drawerSection()];
    const page = $('[data-cart-page]');
    if (page) ids.push(page.dataset.sectionId);
    return ids.filter(Boolean);
  };
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

  WD.cart = {
    async add(formData) {
      const sections = cartSections();
      formData.append('sections', sections.join(','));
      formData.append('sections_url', window.location.pathname);
      const res = await fetch(`${WD.routes.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: formData });
      const json = await res.json();
      if (!res.ok || json.status) throw new Error(json.description || json.message || WD.strings.error);
      WD.cart.render(json.sections);
      await WD.cart.refreshCount();
      return json;
    },
    async change(line, quantity) {
      const res = await fetch(`${WD.routes.cartChange}.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: cartSections(), sections_url: window.location.pathname }),
      });
      const json = await res.json();
      if (!res.ok || json.status) throw new Error(json.description || WD.strings.error);
      WD.cart.render(json.sections);
      WD.cart.setCount(json.item_count);
      return json;
    },
    async note(text) {
      await fetch(`${WD.routes.cartUpdate}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: text }) });
    },
    async refreshCount() {
      const res = await fetch(`${WD.routes.cart}.js`, { headers: { Accept: 'application/json' } });
      const json = await res.json();
      WD.cart.setCount(json.item_count);
      return json;
    },
    setCount(n) {
      $$('[data-cart-count]').forEach((el) => { el.textContent = n > 0 ? n : ''; el.dataset.count = n; });
    },
    render(sections) {
      if (!sections) return;
      const d = drawer();
      const dId = drawerSection();
      if (d && sections[dId]) {
        const fresh = parse(sections[dId]).querySelector('[data-cart-panel]');
        const panel = d.querySelector('[data-cart-panel]');
        // the new panel must not slide in again: the drawer is already open,
        // and after every + / - / remove it travelled in from the right anew
        if (fresh && panel) { if (d.open) fresh.style.animation = 'none'; panel.replaceWith(fresh); }
      }
      const page = $('[data-cart-page]');
      if (page && sections[page.dataset.sectionId]) {
        const fresh = parse(sections[page.dataset.sectionId]).querySelector('[data-cart-page]');
        if (fresh) page.replaceWith(fresh);
      }
    },
  };

  /* Say an error where the eye is. A toast outside an open modal sheet sits
     under the top layer and is never seen, so inside the bag (drawer or
     page) the message goes into the bag itself, as an alert. */
  WD.say = (msg, near) => {
    const box = near && near.closest('dialog[open] .sheet__body, [data-cart-page]');
    if (!box) { WD.toast(msg); return; }
    let el = box.querySelector(':scope > [data-sheet-error]');
    if (!el) { el = document.createElement('p'); el.className = 'sheet__error'; el.setAttribute('role', 'alert'); el.dataset.sheetError = ''; box.prepend(el); }
    el.textContent = msg || WD.strings.error;
    el.hidden = false;
  };

  // quantity / remove links inside the drawer and on the cart page
  document.addEventListener('click', async (e) => {
    const a = e.target.closest('[data-line-change]');
    if (!a) return;
    e.preventDefault();
    const line = Number(a.dataset.lineChange);
    const qty = Math.max(0, Number(a.dataset.quantity));
    const row = a.closest('.line');
    row && row.setAttribute('aria-busy', 'true');
    try { await WD.cart.change(line, qty); } catch (err) { WD.say(err.message, a); row && row.removeAttribute('aria-busy'); }
  });
  document.addEventListener('change', async (e) => {
    const input = e.target.closest('[data-line-input]');
    if (input) {
      try { await WD.cart.change(Number(input.dataset.lineInput), Math.max(0, Number(input.value) || 0)); } catch (err) { WD.say(err.message, input); }
      return;
    }
    const note = e.target.closest('[data-cart-note]');
    if (note) WD.cart.note(note.value);
    const auto = e.target.closest('[data-autosubmit]');
    if (auto && auto.form) auto.form.submit();
  });

  // quick add from a product card
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    btn.setAttribute('aria-busy', 'true');
    const fd = new FormData();
    fd.append('id', btn.dataset.quickAdd);
    fd.append('quantity', '1');
    try {
      await WD.cart.add(fd);
      WD.openSheet('wd-cart', btn) || WD.toast(WD.strings.added);
    } catch (err) { WD.say(err.message, btn); }
    btn.removeAttribute('aria-busy');
  });

  // card swatches: swap the image and the quick-add variant, stay on the grid
  document.addEventListener('click', (e) => {
    const sw = e.target.closest('[data-swatch-variant]');
    if (!sw) return;
    const card = sw.closest('[data-card]');
    if (!card) return;
    $$('[data-swatch-variant]', card).forEach((b) => b.setAttribute('aria-pressed', String(b === sw)));
    const img = card.querySelector('.card__img:not(.card__img--back)');
    if (img && sw.dataset.swatchImg) { img.removeAttribute('srcset'); img.src = sw.dataset.swatchImg; }
    const quick = card.querySelector('[data-quick-add]');
    if (quick) quick.dataset.quickAdd = sw.dataset.swatchVariant;
    const link = card.querySelector('.card__link');
    if (link) link.href = `${card.dataset.productUrl}?variant=${sw.dataset.swatchVariant}`;
  });

  /* ── search ─────────────────────────────────────────────────────────────
     The sheet has two states. IDLE (nothing typed): recent searches, popular
     terms, the print families and four cases, all rendered by the server.
     TYPING: Shopify predictive search (products, collections, articles,
     pages and query suggestions), rendered by sections/predictive-search.
     Arrow keys walk every result; Escape goes back to the field, then closes.
     `/` opens search from anywhere that is not a text field. */
  const ps = $('[data-predictive]');
  if (ps) {
    const input = $('[data-predictive-input]', ps);
    const out = $('[data-predictive-results]', ps);
    const idle = $('[data-ps-idle]', ps);
    const status = $('[data-ps-status]', ps);
    const clear = $('[data-ps-clear]', ps);
    const RECENT = 'wd-recent-searches';
    // The full placeholder is cut mid-word on a phone; swap in the short one.
    const long = input.placeholder;
    const narrow = window.matchMedia('(max-width: 599px)');
    const fitPlaceholder = () => { input.placeholder = narrow.matches ? (input.dataset.placeholderShort || long) : long; };
    fitPlaceholder();
    narrow.addEventListener?.('change', fitPlaceholder);
    let timer; let ctrl; let last = '';
    const recent = () => { try { return JSON.parse(store.get(RECENT) || '[]'); } catch (e) { return []; } };
    const remember = (term) => {
      const t = term.trim(); if (t.length < 2) return;
      store.set(RECENT, JSON.stringify([t, ...recent().filter((x) => x.toLowerCase() !== t.toLowerCase())].slice(0, 6)));
    };
    const paintRecent = () => {
      const box = $('[data-ps-recent]', ps); const list = $('[data-ps-recent-list]', ps);
      if (!box || !list) return;
      const r = recent();
      box.hidden = r.length === 0;
      list.innerHTML = '';
      for (const t of r) {
        const li = document.createElement('li');
        const b = document.createElement('button');
        b.type = 'button'; b.className = 'chip'; b.dataset.psTerm = t; b.textContent = t;
        li.appendChild(b); list.appendChild(li);
      }
    };
    const showIdle = () => { out.replaceChildren(idle); idle.hidden = false; status.textContent = ''; ps.classList.remove('is-loading'); idle.querySelector('[data-ps-down]')?.setAttribute('hidden', ''); paintRecent(); };
    const run = async (q) => {
      ctrl && ctrl.abort();
      ctrl = new AbortController();
      ps.classList.add('is-loading');
      try {
        const fields = encodeURIComponent('title,product_type,variants.title,vendor,tag,body');
        const url = `${ps.dataset.predictive}?q=${encodeURIComponent(q)}&resources[type]=product,collection,article,page,query&resources[limit]=6&resources[options][fields]=${fields}&section_id=predictive-search`;
        const res = await fetch(url, { signal: ctrl.signal });
        if (!res.ok) throw new Error(String(res.status));
        const fresh = parse(await res.text()).querySelector('[data-predictive-body]');
        if (q !== input.value.trim()) return;
        const body = fresh ? document.importNode(fresh, true) : null;
        const n = Number(fresh?.dataset.count || 0);
        // nothing found is not a dead end: the popular searches and every case
        if (body && !n) {
          const pop = idle.querySelector('.ps__group:not([data-ps-recent]) .ps__chips');
          if (pop) body.append(pop.cloneNode(true));
          const all = document.createElement('a'); all.className = 'ps__link ps__all'; all.href = WD.routes.allProducts || '/collections/all'; all.textContent = WD.strings.allCases;
          body.append(all);
        }
        out.replaceChildren(...(body ? [body] : []));
        status.textContent = n ? (n === 1 ? WD.strings.resultOne : WD.strings.resultMany.replace('99', n)).replace('§Q§', q) : (fresh?.querySelector('.ps__empty')?.textContent.trim() || '');
      } catch (err) {
        if (err.name === 'AbortError') return;
        // say it: showing the idle screen looked as if nothing had been typed
        showIdle();
        status.textContent = WD.strings.searchDown;
        let note = idle.querySelector('[data-ps-down]');
        if (!note) { note = document.createElement('p'); note.className = 'sheet__error'; note.setAttribute('role', 'alert'); note.dataset.psDown = ''; idle.prepend(note); }
        note.textContent = WD.strings.searchDown; note.hidden = false;
      }
      ps.classList.remove('is-loading');
    };
    input.addEventListener('input', () => {
      const q = input.value.trim();
      clear.hidden = !input.value;
      clearTimeout(timer);
      if (!q) { last = ''; showIdle(); return; }
      if (q === last) return;
      last = q;
      timer = setTimeout(() => run(q), 160);
    });
    const setTerm = (t) => { input.value = t; clear.hidden = false; input.focus(); last = ''; input.dispatchEvent(new Event('input')); };
    ps.addEventListener('click', (e) => {
      const term = e.target.closest('[data-ps-term]');
      if (term) { setTerm(term.dataset.psTerm); return; }
      if (e.target.closest('[data-ps-clear]')) { input.value = ''; clear.hidden = true; last = ''; showIdle(); input.focus(); return; }
      if (e.target.closest('[data-ps-clear-recent]')) { store.set(RECENT, '[]'); paintRecent(); input.focus(); return; }
      if (e.target.closest('[data-ps-item]') && input.value.trim()) remember(input.value);
    });
    $('[data-ps-form]', ps)?.addEventListener('submit', () => remember(input.value));
    // arrow keys: field -> results -> back
    ps.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const items = $$('[data-ps-item], [data-ps-term], .ps__product, .ps__list a', out).filter((el, i, arr) => arr.indexOf(el) === i && el.offsetParent !== null);
      if (!items.length) return;
      const i = items.indexOf(document.activeElement);
      e.preventDefault();
      if (e.key === 'ArrowDown') (i < 0 ? items[0] : items[Math.min(items.length - 1, i + 1)]).focus();
      else if (i <= 0) input.focus(); else items[i - 1].focus();
    });
    // `/` opens search (not while typing in a field)
    document.addEventListener('keydown', (e) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.target.closest('input, textarea, select, [contenteditable]')) return;
      e.preventDefault();
      WD.openSheet('wd-search', $('[data-sheet-open="wd-search"]'));
    });
    paintRecent();
  }

  /* ── header: desktop disclosure panels ────────────────────────────────────
     Click toggles (touch, keyboard, and the no-hover case). With a fine
     pointer the panels also open on hover, with intent: 140ms before opening
     so a pointer crossing the nav on its way elsewhere opens nothing, and a
     250ms grace on leaving so the gap between trigger and panel never closes
     it. Moving to a neighbouring trigger while one is open switches at once.
     Keyboard: ArrowDown from a trigger enters its panel; Escape closes and
     puts focus back on the trigger. Scrolling a page's worth closes it. */
  const openPanel = (b, focusFirst = false) => {
    closePanels(b);
    b.setAttribute('aria-expanded', 'true');
    const p = document.getElementById(b.getAttribute('aria-controls'));
    if (p) { p.hidden = false; if (focusFirst) p.querySelector('a[href]')?.focus(); }
  };
  const closePanels = (except) => $$('[data-disclosure]').forEach((b) => {
    if (b === except) return;
    b.setAttribute('aria-expanded', 'false');
    const p = document.getElementById(b.getAttribute('aria-controls'));
    if (p) p.hidden = true;
  });
  const openTrigger = () => $('[data-disclosure][aria-expanded="true"]');
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-disclosure]');
    if (b) {
      if (b.getAttribute('aria-expanded') === 'true' && !b.dataset.hoverOpened) closePanels();
      else openPanel(b);
      delete b.dataset.hoverOpened;
      return;
    }
    if (!e.target.closest('[data-panel]')) closePanels();
  });
  document.addEventListener('keydown', (e) => {
    const t = openTrigger();
    if (e.key === 'Escape' && t) { closePanels(); t.focus(); return; }
    const b = e.target.closest?.('[data-disclosure]');
    if (b && e.key === 'ArrowDown') { e.preventDefault(); openPanel(b, true); }
  });
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
  const nav = $('.hdr__nav');
  if (nav) {
    let openT; let closeT;
    nav.addEventListener('pointerover', (e) => {
      if (!fine.matches || e.pointerType !== 'mouse') return;
      clearTimeout(closeT);
      const b = e.target.closest('[data-disclosure]');
      clearTimeout(openT); // passing over anything else cancels a pending open
      if (!b) return;
      const now = openTrigger();
      const go = () => { if (b.getAttribute('aria-expanded') !== 'true') { b.dataset.hoverOpened = '1'; openPanel(b); setTimeout(() => delete b.dataset.hoverOpened, 400); } };
      if (now && now !== b) go(); else openT = setTimeout(go, 140);
    });
    nav.addEventListener('pointerout', (e) => {
      if (!fine.matches || e.pointerType !== 'mouse' || nav.contains(e.relatedTarget)) return;
      clearTimeout(openT);
      closeT = setTimeout(() => { if (!nav.matches(':hover') && !nav.contains(document.activeElement)) closePanels(); }, 250);
    });
    let y0 = 0;
    window.addEventListener('scroll', () => {
      if (!openTrigger()) { y0 = window.scrollY; return; }
      if (Math.abs(window.scrollY - y0) > window.innerHeight * 0.6) closePanels();
    }, { passive: true });
  }
  const header = $('[data-header]');
  if (header) {
    const setH = () => document.documentElement.style.setProperty('--header-h', `${header.offsetHeight}px`);
    setH();
    window.addEventListener('resize', setH, { passive: true });
  }

  /* ── announcement bar ──────────────────────────────────────────────────── */
  const initAnnouncement = (ann) => {
    if (!ann || ann.dataset.wdInit) return;
    ann.dataset.wdInit = '1';
    const key = `wd-announce-${ann.dataset.announce}`;
    if (!window.Shopify?.designMode && store.get(key) === 'closed') ann.hidden = true;
    const msgs = $$('.announce__msg', ann);
    let i = 0;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (msgs.length > 1 && !reduce) {
      setInterval(() => {
        if (!document.contains(ann) || ann.matches(':hover, :focus-within')) return;
        msgs[i].inert = true;
        i = (i + 1) % msgs.length;
        msgs[i].inert = false;
      }, 4500);
    }
    $('[data-announce-close]', ann)?.addEventListener('click', () => { ann.hidden = true; store.set(key, 'closed'); });
  };
  $$('[data-announce]').forEach(initAnnouncement);

  /* ── hero slideshow ────────────────────────────────────────────────────── */
  const initHero = (hero) => {
    if (!hero || hero.dataset.wdInit) return;
    hero.dataset.wdInit = '1';
    const slides = $$('[data-slide]', hero);
    const dots = $$('[data-hero-dot]', hero);
    const pause = $('[data-hero-pause]', hero);
    if (slides.length < 2) return;
    let i = 0; let timer = null; let paused = window.matchMedia('(prefers-reduced-motion: reduce)').matches; let visible = true;
    const interval = (parseFloat(getComputedStyle(hero).getPropertyValue('--hero-interval')) || 7) * 1000;
    const show = (n) => {
      slides[i].classList.remove('is-active'); slides[i].inert = true;
      dots[i]?.removeAttribute('aria-current');
      i = (n + slides.length) % slides.length;
      const img = slides[i].querySelector('img[loading="lazy"]');
      if (img) img.loading = 'eager';
      slides[i].classList.add('is-active'); slides[i].inert = false;
      dots[i]?.setAttribute('aria-current', 'true');
    };
    const tick = () => { clearTimeout(timer); if (!paused && visible && !document.hidden) timer = setTimeout(() => { show(i + 1); tick(); }, interval); };
    // warm the next image so the first fade never shows a half-loaded frame
    const warm = slides[1]?.querySelector('img'); if (warm) warm.loading = 'eager';
    dots.forEach((d) => d.addEventListener('click', () => { show(Number(d.dataset.heroDot)); tick(); }));
    const setPaused = (p) => {
      paused = p;
      if (pause) {
        pause.setAttribute('aria-label', p ? WD.strings.play : WD.strings.pause);
        pause.innerHTML = p ? '<svg class="icon icon--s" viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5.5v13l11-6.5-11-6.5Z"/></svg>' : '<svg class="icon icon--s" viewBox="0 0 24 24" aria-hidden="true"><path d="M9 6v12M15 6v12"/></svg>';
      }
      hero.classList.toggle('is-paused', p);
      tick();
    };
    pause?.addEventListener('click', () => setPaused(!paused));
    if (paused) setPaused(true);
    new IntersectionObserver(([en]) => { visible = en.isIntersecting; tick(); }).observe(hero);
    document.addEventListener('visibilitychange', tick);
    let x0 = null;
    hero.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
    hero.addEventListener('touchend', (e) => {
      if (x0 === null) return;
      const dx = e.changedTouches[0].clientX - x0; x0 = null;
      if (Math.abs(dx) > 40) { show(i + (dx < 0 ? 1 : -1)); tick(); }
    });
    tick();
    // Theme editor: selecting a slide block shows it and holds it there.
    hero.addEventListener('shopify:block:select', (e) => {
      const n = slides.findIndex((s) => s.contains(e.target) || s === e.target);
      if (n >= 0) { paused = true; show(n); tick(); }
    });
    hero.addEventListener('shopify:block:deselect', () => { paused = window.matchMedia('(prefers-reduced-motion: reduce)').matches; tick(); });
  };
  $$('[data-hero]').forEach(initHero);

  /* ── THEME EDITOR ──────────────────────────────────────────────────────
     Customize re-renders a section in place when it is edited, so anything
     bound at page load is gone from the new markup. Re-initialise per section
     on shopify:section:load (delegated listeners above need nothing). */
  document.addEventListener('shopify:section:load', (e) => {
    $$('[data-hero]', e.target).forEach(initHero);
    $$('[data-announce]', e.target).forEach(initAnnouncement);
    const hdr = $('[data-header]', e.target);
    if (hdr) document.documentElement.style.setProperty('--header-h', `${hdr.offsetHeight}px`);
  });

  /* ── collection: filters submit themselves (wd-collection.js does the rest) */
  document.addEventListener('change', (e) => {
    const f = e.target.closest('[data-filter-form]');
    if (f && !WD.filtersEnhanced) f.requestSubmit ? f.requestSubmit() : f.submit();
  });
})();
